// Renders the documentation data into the overlay: a table of contents, one
// article per component, and the bibliography. Built once at startup.

import { SECTIONS, REFERENCES, refByKey } from "./docs";

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`missing element ${sel}`);
  return el as T;
};

const el = (tag: string, cls?: string, text?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
};

export function renderDocs(): void {
  const toc = $("#docs-toc");
  const content = $("#docs-content");
  toc.textContent = "";
  content.textContent = "";

  for (const s of SECTIONS) {
    const link = el("a", "toc-link", s.title);
    (link as HTMLAnchorElement).href = `#doc-${s.id}`;
    link.onclick = (ev) => {
      ev.preventDefault();
      scrollToSection(s.id);
    };
    toc.appendChild(link);

    const art = el("section", "doc-section");
    art.id = `doc-${s.id}`;
    art.appendChild(el("h3", undefined, s.title));
    art.appendChild(el("p", "doc-tagline", s.tagline));
    for (const p of s.body) art.appendChild(el("p", "doc-para", p));

    if (s.algorithm) {
      art.appendChild(el("h4", undefined, s.algorithm.title));
      const ol = el("ol", "doc-steps");
      for (const step of s.algorithm.steps) {
        ol.appendChild(el("li", undefined, step));
      }
      art.appendChild(ol);
    }

    if (s.costs) {
      const table = el("table", "doc-costs");
      for (const [k, v] of s.costs) {
        const tr = el("tr");
        tr.appendChild(el("td", "cost-key", k));
        tr.appendChild(el("td", undefined, v));
        table.appendChild(tr);
      }
      art.appendChild(table);
    }

    if (s.source) {
      art.appendChild(el("p", "doc-source", `Source: ${s.source}`));
    }

    if (s.refs?.length) {
      const p = el("p", "doc-refs");
      p.appendChild(el("span", "refs-label", "Further reading: "));
      s.refs.forEach((key, i) => {
        const r = refByKey(key);
        if (!r) return;
        const a = el("a", "ref-chip", `${r.authors.split(",")[0].trim()} ${r.year}`);
        (a as HTMLAnchorElement).href = `#ref-${key}`;
        a.onclick = (ev) => {
          ev.preventDefault();
          scrollToRef(key);
        };
        p.appendChild(a);
        if (i < s.refs!.length - 1) p.appendChild(document.createTextNode(" "));
      });
      art.appendChild(p);
    }
    content.appendChild(art);
  }

  // Bibliography
  const refLink = el("a", "toc-link", "References");
  (refLink as HTMLAnchorElement).href = "#doc-references";
  refLink.onclick = (ev) => {
    ev.preventDefault();
    scrollToSection("references");
  };
  toc.appendChild(refLink);

  const bib = el("section", "doc-section");
  bib.id = "doc-references";
  bib.appendChild(el("h3", undefined, "References"));
  bib.appendChild(
    el(
      "p",
      "doc-tagline",
      "Where these ideas come from, and what each one contributes here.",
    ),
  );
  for (const r of REFERENCES) {
    const item = el("div", "ref-item");
    item.id = `ref-${r.key}`;
    const cite = el("p", "ref-cite");
    cite.appendChild(el("span", "ref-authors", r.authors + ". "));
    cite.appendChild(el("span", "ref-title", `“${r.title}.” `));
    cite.appendChild(el("span", "ref-venue", `${r.venue}.`));
    item.appendChild(cite);
    item.appendChild(el("p", "ref-why", r.relevance));
    bib.appendChild(item);
  }
  content.appendChild(bib);
}

function scrollToSection(id: string): void {
  const target = document.getElementById(`doc-${id}`);
  target?.scrollIntoView({ behavior: "smooth", block: "start" });
  for (const a of document.querySelectorAll(".toc-link")) {
    a.classList.toggle(
      "active",
      (a as HTMLAnchorElement).getAttribute("href") === `#doc-${id}`,
    );
  }
}

function scrollToRef(key: string): void {
  const target = document.getElementById(`ref-${key}`);
  target?.scrollIntoView({ behavior: "smooth", block: "center" });
  target?.classList.add("ref-flash");
  setTimeout(() => target?.classList.remove("ref-flash"), 1200);
}

export function openDocsAt(id: string): void {
  $("#docs-overlay").hidden = false;
  // Jump without animation on open, so the section is already in view.
  const target = document.getElementById(`doc-${id}`);
  if (target) {
    // behavior:"instant" overrides the container's CSS scroll-behavior, which
    // would otherwise animate this jump from wherever the reader last was.
    $("#docs-content").scrollTo({
      top: Math.max(0, target.offsetTop - 8),
      behavior: "instant",
    });
    for (const a of document.querySelectorAll(".toc-link")) {
      a.classList.toggle(
        "active",
        (a as HTMLAnchorElement).getAttribute("href") === `#doc-${id}`,
      );
    }
  }
}

export function closeDocs(): void {
  $("#docs-overlay").hidden = true;
}
